import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import getSalesTotal from '../../utils/getSalesTotal';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import { SalesInfo, defaultSalesInfo } from '../../interfaces/sales-info';
import { updateSalesTotalData } from '../../DumbleData';
import { Pages } from '../../App';
import { ConfigContext } from '../../providers/ConfigContext';
import { RedButton } from '../../components/Button';
import SalesTotalModal from './SalesTotalModal';
import useCloseFair from '../../utils/putCloseFair';
import Bookfair from '../../interfaces/bookfair-info';

interface Props {
	bfInfo: any;
	setBookfairInfo: React.Dispatch<React.SetStateAction<Bookfair>>;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const [acquireSalesTotal] = getSalesTotal();

const SalesTotalContent = ({ bfInfo, setBookfairInfo, setCurrentPage }: Props) => {
	const { apiBase } = useContext(ConfigContext);
	const [status, setStatus] = useState<number>(200);
	const [closeFair] = useCloseFair();
	const [salesInfo, setSalesInfo] = useState<SalesInfo>(defaultSalesInfo);

	// Get Sales Totals
	useEffect(() => {
		const getSalesTotalInfo = async () => {
			const { data, error } = await acquireSalesTotal(apiBase);
			if (error) {
				console.error(error);
				setCurrentPage(Pages.Error);
			} else if (data != null) {
				const parsedInfo = {
					ccAndeWalletQty: data.tenderTotals.ccAndeWallets.quantity,
					ccAndeWalletAmount: data.tenderTotals.ccAndeWallets.amount,
					cashAndChecksQty: data.tenderTotals.cashAndChecks.quantity,
					cashAndChecksAmount: data.tenderTotals.cashAndChecks.amount,
					poQty: data.tenderTotals.po.quantity,
					poAmount: data.tenderTotals.po.amount,
					sdQty: data.tenderTotals.sd.quantity,
					sdAmount: data.tenderTotals.sd.amount,
					sdCashEquivalentQty: data.tenderTotals.sdCashEquivalent.quantity,
					sdCashEquivalentAmount: data.tenderTotals.sdCashEquivalent.amount,
					totalQty: data.tenderTotals.total.quantity,
					totalAmount: data.tenderTotals.total.amount,
					taxExemptSales: data.grossSales.taxExemptSales,
					taxableSales: data.grossSales.taxableSales,
					grossTotal: data.grossSales.total,
					taxTotal: data.grossSales.taxTotal,
					taxableSalesLessTax: data.grossSales.taxableSalesLessTax,
					taxRate: data.taxRate,
					stfRedeemed: data.netSales.shareTheFairRedeemed,
				};
				updateSalesTotalData(parsedInfo);
				setSalesInfo(parsedInfo);
			}
		};

		getSalesTotalInfo();
	}, [setCurrentPage, apiBase]);

	const currDate = new Date().setHours(0, 0, 0, 0);
	const endDate = new Date(bfInfo.endDate).setHours(0, 0, 0, 0);
	const isFairEnded = bfInfo.isFairEnded;
	const isFairBeforeEndDate = currDate < endDate;
	const isButtonDisabled = isFairBeforeEndDate || status !== 200;
	const [showModal, setShowModal] = useState(false);

	const handleCloseFair = async () => {
		window._satellite.track('upload-sales-closeout-fair-submit');
		const { data, error } = await closeFair(apiBase);
		if (error) {
			console.error(error);
			setStatus(error.status);
			setShowModal(false);
		} else if (data != null) {
			setShowModal(false);
			setBookfairInfo((prevInfo) => ({
				...prevInfo,
				isFairEnded: true,
			}));
		}
	};

	return (
		<>
			{showModal && (
				<SalesTotalModal
					handleModal={handleCloseFair}
					closeModal={() => {
						setShowModal(false);
					}}
				/>
			)}
			<SalesTotal>
				<SalesTotalWrapper>
					<SalesHeading>Sales Totals</SalesHeading>
					<SalesCopy>Totals include Payment Portal transactions only.</SalesCopy>
					<SalesTable>
						<SalesSubheading>Scholastic Dollars</SalesSubheading>
						<SalesRow>
							<span>Total Scholastic Dollars Redeemed</span>
							<span>{formatCurrencyNumber(salesInfo.sdAmount)}</span>
						</SalesRow>
						<SalesRow>
							<strong>Cash Equivalent (Redeemed x 50%)</strong>
							<strong>{formatCurrencyNumber(salesInfo.sdCashEquivalentAmount)}</strong>
						</SalesRow>
					</SalesTable>
					<SalesTable>
						<SalesSubheading>Tender Totals</SalesSubheading>
						<SalesRow>
							<span>Tender</span>
							<span>Qty</span>
							<span>Amt</span>
						</SalesRow>
						<SalesRow>
							<span>Cash & Checks</span>
							<span>{salesInfo.cashAndChecksQty}</span>
							<span>{formatCurrencyNumber(salesInfo.cashAndChecksAmount)}</span>
						</SalesRow>
						<SalesRow>
							<span>Credit Cards & eWallet</span>
							<span>{salesInfo.ccAndeWalletQty}</span>
							<span>{formatCurrencyNumber(salesInfo.ccAndeWalletAmount)}</span>
						</SalesRow>
						<SalesRow>
							<span>Purchase Orders</span>
							<span>{salesInfo.poQty}</span>
							<span>{formatCurrencyNumber(salesInfo.poAmount)}</span>
						</SalesRow>
						<SalesRow>
							<span>Scholastic Dollars (Cash Equivalent)</span>
							<span>{salesInfo.sdCashEquivalentQty}</span>
							<span>{formatCurrencyNumber(salesInfo.sdCashEquivalentAmount)}</span>
						</SalesRow>
						<SalesRow>
							<strong>Total</strong>
							<strong>{salesInfo.totalQty}</strong>
							<strong>{formatCurrencyNumber(salesInfo.totalAmount)}</strong>
						</SalesRow>
					</SalesTable>
					<SalesTable>
						<SalesSubheading>Gross Sales</SalesSubheading>
						<SalesRow>
							<span>Tax-Exempt Sales</span>
							<span>{formatCurrencyNumber(salesInfo.taxExemptSales)}</span>
						</SalesRow>
						<SalesRow>
							<span>Taxable Sales</span>
							<span>{formatCurrencyNumber(salesInfo.taxableSales)}</span>
						</SalesRow>
						<SalesRow>
							<strong>Gross Sales Total</strong>
							<strong>{formatCurrencyNumber(salesInfo.grossTotal)}</strong>
						</SalesRow>
						<SalesRow>
							<span>Tax Total ({salesInfo.taxRate}%)</span>
							<span>{formatCurrencyNumber(salesInfo.taxTotal)}</span>
						</SalesRow>
						<SalesRow>
							<span>Taxable Sales (Less Sales Tax)</span>
							<span>{formatCurrencyNumber(salesInfo.taxableSalesLessTax)}</span>
						</SalesRow>
					</SalesTable>
					<SalesTable>
						<SalesSubheading>Net Sales</SalesSubheading>
						<SalesRow>
							<span>
								Share the Fair Funds Redeemed <br /> (Included in Gross Sales)
							</span>
							<span>{formatCurrencyNumber(salesInfo.stfRedeemed)}</span>
						</SalesRow>
					</SalesTable>
				</SalesTotalWrapper>
				<ButtonDiv>
					{!isFairEnded && (
						<StyledRedButton
							handleClick={() => {
								window._satellite.track('upload-sales-closeout-fair');
								setShowModal(true);
							}}
							disabled={isButtonDisabled}
						>
							Upload totals and close out the fair
						</StyledRedButton>
					)}
					{status !== 200 && <ErrorText>Something went wrong, try again.</ErrorText>}
				</ButtonDiv>
			</SalesTotal>
		</>
	);
};

const SalesTotal = styled.div`
	padding: 32px 0 45px 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	@media only screen and (max-width: 720px) {
		padding: 32px 20px 45px 20px;
	}
`;
const SalesTotalWrapper = styled.div`
	width: 440px;
	text-align: left;
	display: flex;
	flex-direction: column;
	gap: 32px;
	@media only screen and (max-width: 720px) {
		width: 100%;
	}
`;
const SalesHeading = styled.h1`
	margin: 0;
	font-size: 24px;
	line-height: 28px;
	font-weight: 400;
`;
const SalesSubheading = styled.h2`
	margin: 0;
	padding-bottom: 8px;
	font-size: 16px;
	font-weight: 600;
	line-height: 19px;
`;
const SalesCopy = styled.p`
	margin: 0;
	font-size: 13px;
	line-height: 15px;
`;
const SalesTable = styled.div`
	display: block;
	div:last-child {
		padding-bottom: 0;
	}
`;
const SalesRow = styled.div`
	border-top: 1px solid #d7d7d7;
	padding: 8px 0;
	font-size: 13px;
	line-height: 15px;
	display: flex;
	justify-content: space-between;
	strong {
		font-weight: 600;
	}
	span:first-child,
	strong:first-child {
		width: 215px;
	}
	span:last-child,
	strong:last-child {
		min-width: 70px;
		text-align: right;
	}
`;

const ButtonDiv = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	margin-top: 65px;
	width: 340px;

	@media only screen and (max-width: 380px) {
		width: 100%;
	}
`;

const StyledRedButton = styled(RedButton)`
	width: 100%;
	margin: 0;
`;

const ErrorText = styled.p`
	font-size: 13px;
	font-style: normal;
	font-weight: 400;
	line-height: 15px;
	margin-top: 23px;
	text-align: left;
	color: #e81111;
	margin-top: 20px;
`;

export default SalesTotalContent;
